import { MailOutline, EmojiEventsOutlined, NotificationsActiveOutlined } from '@mui/icons-material';
import { Badge, IconButton, Stack, Tooltip } from '@mui/material';
import { DirectMessageThread, PhioengagePatientProfilev2, PhioengageScheduledTask } from '../types';
import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestoreInstance } from 'config/ApiService';
import { orange } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import { setUserActivity } from 'services/trackUserActivity';

export const ActionPanel = ({ data }: { data: PhioengagePatientProfilev2 }) => {
    const [openThread, setOpenThread] = useState<DirectMessageThread | null>(null);
    const [openTasks, setOpenTasks] = useState<PhioengageScheduledTask[]>([]);

    const navigate = useNavigate();

    // attach listener to message thread
    useEffect(() => {
        const unsubToThreads = onSnapshot(
            query(
                collection(
                    firestoreInstance(),
                    `phioengage_user_profiles/${data.id}/direct_message_threads`
                ),
                where('status', '==', 'open')
            ),
            (snapshot) => {
                if (snapshot.empty) {
                    setOpenThread(null);
                    return;
                }
                setOpenThread({
                    ...snapshot.docs[0].data(),
                    id: snapshot.docs[0].id
                } as DirectMessageThread);
            }
        );
        return () => {
            unsubToThreads();
        };
        // eslint-disable-next-line
    }, [data.id]);

    // attach listener to open tasks
    useEffect(() => {
        const unsubToTasks = onSnapshot(
            query(
                collection(
                    firestoreInstance(),
                    `phioengage_user_profiles/${data.id}/scheduled_tasks`
                ),
                where('archived', '==', false),
                where('reviewed', '==', false),
                where('programme_id', '==', data.programme_id),
                where('review_date', '<=', new Date())
            ),
            (snapshot) => {
                if (snapshot.empty) {
                    setOpenTasks([]);
                    return;
                }
                const tasks = snapshot.docs.map((doc) => doc.data() as PhioengageScheduledTask);
                setOpenTasks(tasks);
            }
        );
        return () => {
            unsubToTasks();
        };
        // eslint-disable-next-line
    }, [data.id]);

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            {openThread && openThread.unread_count?.support > 0 && (
                <Tooltip title="Unread messages">
                    <IconButton
                        aria-label="unread messages"
                        onClick={() => {
                            navigate(`/dashboard/phioengage-profile/${data.id}`, {
                                state: { activeTab: '2', threadId: openThread.id }
                            });
                            setUserActivity({
                                patientId: data.id,
                                eventName: 'tab_visited',
                                eventDetails: {
                                    tab_name: 'chat',
                                    visited_from: 'unread_messages_button'
                                }
                            });
                        }}
                    >
                        <Badge badgeContent={openThread.unread_count?.support} color="error">
                            <MailOutline color="info" />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}
            {openTasks.length > 0 && (
                <Tooltip title="Scheduled reviews">
                    <IconButton
                        onClick={() => {
                            navigate(`/dashboard/phioengage-profile/${data.id}`, {
                                state: { activeTab: '4' }
                            });
                            setUserActivity({
                                patientId: data.id,
                                eventName: 'tab_visited',
                                eventDetails: {
                                    tab_name: 'scheduled_reviews',
                                    visited_from: 'scheduled_reviews_button'
                                }
                            });
                        }}
                        aria-label="scheduled reviews"
                    >
                        <NotificationsActiveOutlined sx={{ color: orange[500] }} />
                    </IconButton>
                </Tooltip>
            )}
            {openThread && openThread.unread_count?.support <= 0 && (
                <Tooltip title="Open chat thread">
                    <IconButton
                        onClick={() =>
                            navigate(`/dashboard/phioengage-profile/${data.id}`, {
                                state: { activeTab: '2', threadId: openThread.id }
                            })
                        }
                        aria-label="open chat thread"
                    >
                        <MailOutline color="info" />
                    </IconButton>
                </Tooltip>
            )}
            {data.has_completed_programme && (
                <Tooltip title="Patient has completed the programme">
                    <EmojiEventsOutlined color="success" />
                </Tooltip>
            )}
        </Stack>
    );
};
